import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import TicketDetailView from '../TicketDetailView';
import AssignTicketToAgentList from './componentes/AssignTicketToAgentList';
import AppContext from 'context/Context';
import {
    TicketsFilterTabs,
    TicketCardItem,
    TicketsEmptyState,
    TicketsLoadingState,
    AgentFilterSelect,
    EtiquetaFilterSelect,
    NivelFilterSelect,
    TicketsPagination
} from './componentes';
import { useTicketsList } from './hooks/useTicketsList';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faTimes,
    faSlidersH,
    faUserCheck,
    faTags,
    faLayerGroup,
    faUsers
} from '@fortawesome/free-solid-svg-icons';

const SUPPORT_LEVEL_LABELS = {
    '0': 'Sin nivel',
    '1': 'Nivel 1 Básico',
    '2': 'Nivel 2 Avanzado',
    '3': 'Nivel 3 Desarrollo',
    '4': 'Nivel 4 Externo',
    '5': 'Nivel 5 Cotización',
    '6': 'Nivel 6 Ventas'
};

const formatTitleCase = str => {
    if (!str) return '';
    return str
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

const TicketsView = ({ onTicketSelect, selectedTicket, setSelectedTicket, onTicketDetailLoaded, onInitialReady }) => {
    const [internalSelectedTicket, setInternalSelectedTicket] = useState(null);
    const { config: { isDark } = {} } = useContext(AppContext);
    const [filterTab, setFilterTab] = useState('en_progreso');
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [agents, setAgents] = useState([]);
    const [etiquetas, setEtiquetas] = useState([]);
    const [selectedEtiquetaId, setSelectedEtiquetaId] = useState('');
    const [selectedNivelId, setSelectedNivelId] = useState('');
    const [isLoadingEtiquetas, setIsLoadingEtiquetas] = useState(false);
    const { token, nombreCompleto, idUsuario } = useToken();
    const [selectedAgentId, setSelectedAgentId] = useState(idUsuario ? String(idUsuario) : '');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const hasStartedLoadingRef = useRef(false);
    const hasAutoCheckedTabRef = useRef(false);
    const hasInitializedAgentRef = useRef(false);
    const etiquetasLoadedRef = useRef(false);
    const { showSuccessToast, showErrorToast } = useToast();

    // Sincronizar selectedAgentId por defecto con el asesor logueado al iniciar
    useEffect(() => {
        if (idUsuario && !hasInitializedAgentRef.current) {
            hasInitializedAgentRef.current = true;
            setSelectedAgentId(String(idUsuario));
        }
    }, [idUsuario]);

    // Debounce de la búsqueda para optimizar peticiones
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Estados para el Modal de Asignación de Ticket
    const [ticketToAssign, setTicketToAssign] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);

    const currentTicket = selectedTicket !== undefined ? selectedTicket : internalSelectedTicket;
    const isViewingDetail = Boolean(currentTicket);

    // Solo consultar la lista de tickets a la BD si NO estamos dentro del detalle de un ticket
    const {
        tickets,
        counts,
        isLoading,
        page,
        setPage,
        pageSize,
        setPageSize,
        totalRegistros,
        totalPages,
        reloadTickets,
        markTicketAsRead
    } = useTicketsList(
        filterTab,
        selectedAgentId,
        !isViewingDetail,
        20,
        onInitialReady,
        null,
        null,
        debouncedSearchTerm,
        selectedEtiquetaId,
        selectedNivelId
    );

    // Si no hay tickets en progreso al terminar la primera carga, cambiar a pendientes por defecto
    useEffect(() => {
        if (isLoading) {
            hasStartedLoadingRef.current = true;
        }
        if (hasStartedLoadingRef.current && !isLoading && !hasAutoCheckedTabRef.current) {
            if (counts && counts.enProgreso === 0) {
                setFilterTab('pendientes');
            }
            hasAutoCheckedTabRef.current = true;
        }
    }, [isLoading, counts]);

    // Cargar catálogo de incidencias/etiquetas activas
    const fetchEtiquetas = useCallback(async () => {
        if (!token || etiquetasLoadedRef.current) return;
        setIsLoadingEtiquetas(true);
        try {
            const resp = await fetch('/api/SupportEngine/GetEtiquetas?tab=en_progreso', {
                headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
            });
            if (resp.ok) {
                const result = await resp.json();
                const data = result.data || (Array.isArray(result) ? result : []);
                setEtiquetas(Array.isArray(data) ? data : []);
                etiquetasLoadedRef.current = true;
            }
        } catch {
            // Silencioso
        } finally {
            setIsLoadingEtiquetas(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEtiquetas();
    }, [fetchEtiquetas]);

    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const isFetchingAgentsRef = useRef(false);
    const agentsLoadedRef = useRef(false);

    // Cargar lista de agentes activos
    const fetchAgents = useCallback(async () => {
        if (!token || isFetchingAgentsRef.current || agentsLoadedRef.current) return;
        isFetchingAgentsRef.current = true;
        setIsLoadingAgents(true);
        try {
            const response = await fetch('/api/SupportEngine/GetAgentsList', {
                headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
            });
            if (response.ok) {
                const result = await response.json();
                const data = result.data || (Array.isArray(result) ? result : []);
                setAgents(Array.isArray(data) ? data : []);
                agentsLoadedRef.current = true;
            }
        } catch {
            // Fallback silencioso
        } finally {
            isFetchingAgentsRef.current = false;
            setIsLoadingAgents(false);
        }
    }, [token]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    // Abre el modal de asignación y asegura que la lista de asesores esté cargada
    const handleOpenAssignModal = (ticket) => {
        if (agents.length === 0) {
            fetchAgents();
        }
        setTicketToAssign(ticket);
        setShowAssignModal(true);
    };

    // Ejecuta la asignación cuando el usuario confirma en el modal
    const handleConfirmAssignTicket = async (ticket, selectedAgent) => {
        try {
            const targetAgent = selectedAgent || {
                idUsuario: Number(idUsuario),
                vNombreCompleto: nombreCompleto || 'Asesor'
            };

            const payload = {
                idTicket: ticket.idTicket,
                idUsuarioAsignado: Number(targetAgent.idUsuario),
                vUpdatedUser: targetAgent.vNombreCompleto || nombreCompleto || 'Asesor'
            };

            const response = await fetch('/api/SupportEngine/AssignTicketToAgent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + token
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Error al asignar el ticket');
            }

            if (showSuccessToast) {
                showSuccessToast(
                    'Asignación exitosa',
                    `Ticket #${ticket.idTicket} asignado a ${targetAgent.vNombreCompleto}. Saludo de bienvenida enviado.`
                );
            }

            reloadTickets();
        } catch (err) {
            if (showErrorToast) {
                showErrorToast('Error de asignación', err.message);
            }
        }
    };


    const handleSelectTicket = ticket => {
        if (!ticket) return;
        const ticketId = ticket.idTicket || ticket.id;
        if (markTicketAsRead) {
            markTicketAsRead(ticketId);
        }
        window.open(`/support-engine/ticket/${ticketId}`, '_blank');
    };

    const handleBackToList = () => {
        const currentTicketId = currentTicket?.idTicket || currentTicket?.id;
        if (currentTicketId && markTicketAsRead) {
            markTicketAsRead(currentTicketId);
        }
        if (setSelectedTicket) {
            setSelectedTicket(null);
        } else {
            setInternalSelectedTicket(null);
        }
        if (onTicketSelect) {
            onTicketSelect(null);
        }
    };

    // Detección de filtros activos
    const isCustomAgent = idUsuario ? String(selectedAgentId) !== String(idUsuario) : Boolean(selectedAgentId);
    const isCustomEtiqueta = Boolean(selectedEtiquetaId);
    const isCustomNivel = Boolean(selectedNivelId !== '' && selectedNivelId !== null && selectedNivelId !== undefined);
    const activeFiltersCount = (isCustomAgent ? 1 : 0) + (isCustomEtiqueta ? 1 : 0) + (isCustomNivel ? 1 : 0);

    if (currentTicket) {
        return (
            <TicketDetailView
                ticket={currentTicket}
                onBack={handleBackToList}
                onTicketUpdated={reloadTickets}
                onTicketDetailLoaded={(data) => {
                    if (onTicketDetailLoaded) onTicketDetailLoaded(data);
                    if (onInitialReady) onInitialReady();
                }}
                onSelectTicket={handleSelectTicket}
            />
        );
    }

    return (
        <div className="tickets-view-container">
            {/* 1. Vista Escritorio (Barra en una sola fila completa con botón de filtros) */}
            <div
                className="d-none d-md-block rounded-3 py-2 px-3 mb-3 border shadow-sm"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
            >
                <div className="d-flex align-items-center justify-content-between gap-2 gap-xl-3 flex-nowrap w-100">
                    {/* Pestañas de estado (Izquierda) */}
                    <div className="flex-shrink-0">
                        <TicketsFilterTabs
                            filterTab={filterTab}
                            setFilterTab={setFilterTab}
                            counts={counts}
                            isMobile={false}
                        />
                    </div>

                    {/* Buscador, Chips de filtros activos y Botón de Filtros (Derecha) */}
                    <div className="d-flex align-items-center gap-2 flex-nowrap">
                        {/* Buscador de escritorio */}
                        <div className="position-relative flex-shrink-0" style={{ width: '240px' }}>
                            <input
                                type="text"
                                className="form-control form-control-sm ps-4 pe-4 border shadow-none"
                                placeholder="Buscar por ticket o celular..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                                    borderColor: isDark ? '#233142' : '#e2e8f0',
                                    color: isDark ? '#f8fafc' : '#334155',
                                    borderRadius: '8px',
                                    height: '34px',
                                    fontSize: '0.78rem'
                                }}
                            />
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="position-absolute text-muted"
                                style={{
                                    left: '11px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: '0.75rem',
                                    pointerEvents: 'none'
                                }}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm('')}
                                    className="btn btn-link position-absolute p-0 text-muted border-0 shadow-none"
                                    style={{
                                        right: '9px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '0.72rem'
                                    }}
                                    title="Limpiar búsqueda"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>

                        {/* Chip de Asesor activo (si no es el asesor logueado por defecto) */}
                        {isCustomAgent && (
                            <span
                                className="d-inline-flex align-items-center gap-2 flex-shrink-0"
                                style={{
                                    height: '34px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5',
                                    color: isDark ? '#34d399' : '#059669',
                                    border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #a7f3d0',
                                    fontSize: '0.78rem',
                                    fontWeight: 500
                                }}
                            >
                                <FontAwesomeIcon icon={faUserCheck} style={{ fontSize: '0.72rem' }} />
                                <span>
                                    {selectedAgentId === ''
                                        ? 'Todos los asesores'
                                        : formatTitleCase(
                                              agents.find(a => String(a.idUsuario) === String(selectedAgentId))?.vNombreCompleto ||
                                              agents.find(a => String(a.idUsuario) === String(selectedAgentId))?.vNombre ||
                                              `Asesor #${selectedAgentId}`
                                          )}
                                </span>
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    style={{ cursor: 'pointer', fontSize: '0.70rem', opacity: 0.7 }}
                                    onClick={() => setSelectedAgentId(String(idUsuario))}
                                    title="Restablecer a mis métricas"
                                />
                            </span>
                        )}

                        {/* Chip de Incidencia activa */}
                        {isCustomEtiqueta && (
                            <span
                                className="d-inline-flex align-items-center gap-2 flex-shrink-0"
                                style={{
                                    height: '34px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    backgroundColor: isDark ? 'rgba(245, 158, 11, 0.12)' : '#fef3c7',
                                    color: isDark ? '#fbbf24' : '#b45309',
                                    border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid #fde68a',
                                    fontSize: '0.78rem',
                                    fontWeight: 500
                                }}
                            >
                                <FontAwesomeIcon icon={faTags} style={{ fontSize: '0.72rem' }} />
                                <span>{etiquetas.find(e => String(e.idEtiqueta) === String(selectedEtiquetaId))?.vNombre || 'Incidencia'}</span>
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    style={{ cursor: 'pointer', fontSize: '0.70rem', opacity: 0.7 }}
                                    onClick={() => setSelectedEtiquetaId('')}
                                    title="Quitar filtro de incidencia"
                                />
                            </span>
                        )}

                        {/* Chip de Nivel activo */}
                        {isCustomNivel && (
                            <span
                                className="d-inline-flex align-items-center gap-2 flex-shrink-0"
                                style={{
                                    height: '34px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff',
                                    color: isDark ? '#60a5fa' : '#2563eb',
                                    border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #bfdbfe',
                                    fontSize: '0.78rem',
                                    fontWeight: 500
                                }}
                            >
                                <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '0.72rem' }} />
                                <span>{SUPPORT_LEVEL_LABELS[String(selectedNivelId)] || `Nivel ${selectedNivelId}`}</span>
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    style={{ cursor: 'pointer', fontSize: '0.70rem', opacity: 0.7 }}
                                    onClick={() => setSelectedNivelId('')}
                                    title="Quitar filtro de nivel"
                                />
                            </span>
                        )}

                        {/* Botón Tipo Filtro que despliega los filtros de Asesores, Incidencias y Niveles */}
                        <Dropdown autoClose="outside" align="end" className="flex-shrink-0">
                            <Dropdown.Toggle
                                as="button"
                                id="btn-filtros-dropdown"
                                className="btn btn-sm d-inline-flex align-items-center gap-2 border shadow-none"
                                style={{
                                    height: '34px',
                                    borderRadius: '8px',
                                    fontSize: '0.80rem',
                                    fontWeight: 600,
                                    backgroundColor: activeFiltersCount > 0
                                        ? (isDark ? 'rgba(14, 165, 233, 0.15)' : '#e0f2fe')
                                        : (isDark ? '#1e293b' : '#f8fafc'),
                                    borderColor: activeFiltersCount > 0
                                        ? (isDark ? '#0284c7' : '#38bdf8')
                                        : (isDark ? '#233142' : '#e2e8f0'),
                                    color: activeFiltersCount > 0
                                        ? (isDark ? '#38bdf8' : '#0284c7')
                                        : (isDark ? '#cbd5e1' : '#475569')
                                }}
                                title="Filtrar por Asesor, Incidencia o Nivel"
                            >
                                <FontAwesomeIcon icon={faSlidersH} style={{ fontSize: '0.78rem' }} />
                                <span>Filtros</span>
                                {activeFiltersCount > 0 && (
                                    <span
                                        className="badge rounded-pill text-white ms-0.5"
                                        style={{
                                            backgroundColor: '#0284c7',
                                            fontSize: '0.68rem',
                                            padding: '0.22em 0.55em'
                                        }}
                                    >
                                        {activeFiltersCount}
                                    </span>
                                )}
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                                className="p-3 shadow-lg border rounded-3"
                                style={{
                                    minWidth: '320px',
                                    maxWidth: '360px',
                                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                                    borderColor: isDark ? '#233142' : '#e2e8f0',
                                    zIndex: 1060
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom" style={{ borderColor: isDark ? '#1e293b' : '#f1f5f9' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <FontAwesomeIcon icon={faSlidersH} style={{ color: '#0284c7', fontSize: '0.85rem' }} />
                                        <span className="fw-bold" style={{ fontSize: '0.85rem', color: isDark ? '#f8fafc' : '#0f172a' }}>
                                            Filtros de Tickets
                                        </span>
                                    </div>
                                    {activeFiltersCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedEtiquetaId('');
                                                setSelectedNivelId('');
                                                if (idUsuario) setSelectedAgentId(String(idUsuario));
                                            }}
                                            className="btn btn-link btn-sm p-0 text-decoration-none text-muted"
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            Limpiar
                                        </button>
                                    )}
                                </div>

                                <div className="d-flex flex-column gap-3">
                                    {/* 1. Asesor asignado */}
                                    <div className="w-100">
                                        <AgentFilterSelect
                                            selectedAgentId={selectedAgentId}
                                            onSelectAgent={setSelectedAgentId}
                                            agents={agents}
                                            currentUserId={idUsuario}
                                            currentUserName={nombreCompleto}
                                            isLoadingAgents={isLoadingAgents}
                                        />
                                    </div>

                                    {/* 2. Incidencia */}
                                    <div className="w-100">
                                        <EtiquetaFilterSelect
                                            selectedEtiquetaId={selectedEtiquetaId}
                                            onSelectEtiqueta={setSelectedEtiquetaId}
                                            etiquetas={etiquetas}
                                            isLoading={isLoadingEtiquetas}
                                        />
                                    </div>

                                    {/* 3. Nivel de Soporte */}
                                    <div className="w-100">
                                        <NivelFilterSelect
                                            selectedNivelId={selectedNivelId}
                                            onSelectNivel={setSelectedNivelId}
                                        />
                                    </div>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </div>

            {/* 2. Vista Móvil (Pestañas completas + Buscador y Botón Filtros Bottom Sheet) */}
            <div className="d-md-none mb-3 d-flex flex-column gap-2">
                <TicketsFilterTabs
                    filterTab={filterTab}
                    setFilterTab={setFilterTab}
                    counts={counts}
                    isMobile={true}
                />

                <div className="d-flex align-items-center gap-2 w-100">
                    <div className="position-relative flex-grow-1" style={{ minWidth: 0 }}>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-4 pe-4 rounded-pill shadow-none"
                            placeholder="Buscar por celular o # ticket..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{
                                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                borderColor: isDark ? '#233142' : '#e2e8f0',
                                color: isDark ? '#f8fafc' : '#334155',
                                height: '38px',
                                fontSize: '0.82rem'
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="position-absolute text-muted"
                            style={{
                                left: '14px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '0.78rem',
                                pointerEvents: 'none'
                            }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="btn btn-link position-absolute p-0 text-muted border-0 shadow-none"
                                style={{
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: '0.75rem'
                                }}
                                title="Limpiar búsqueda"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowBottomSheet(true)}
                        className="btn btn-sm px-3 rounded-pill d-inline-flex align-items-center gap-2 shadow-none flex-shrink-0"
                        style={{
                            height: '38px',
                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                            border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                            color: isDark ? '#cbd5e1' : '#334155',
                            fontSize: '0.82rem',
                            fontWeight: 600
                        }}
                        title="Abrir Filtros"
                    >
                        <FontAwesomeIcon icon={faSlidersH} style={{ fontSize: '0.78rem', color: '#0284c7' }} />
                        <span>Filtros</span>

                    </button>
                </div>
            </div>

            {showBottomSheet && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end justify-content-center"
                    style={{
                        backgroundColor: 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(3px)',
                        zIndex: 1080,
                        animation: 'fadeIn 0.2s ease-out'
                    }}
                    onClick={() => setShowBottomSheet(false)}
                >
                    <div
                        className="w-100 p-3 rounded-top-4 shadow-lg border-top"
                        style={{
                            maxWidth: '500px',
                            backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                            borderColor: isDark ? '#334155' : '#e2e8f0',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Indicador superior de arrastre */}
                        <div className="d-flex justify-content-center mb-2">
                            <div
                                style={{
                                    width: '38px',
                                    height: '4px',
                                    borderRadius: '2px',
                                    backgroundColor: isDark ? '#475569' : '#cbd5e1'
                                }}
                            />
                        </div>

                        {/* Encabezado del Bottom Sheet */}
                        <div
                            className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom"
                            style={{ borderColor: isDark ? '#1e293b' : '#e2e8f0' }}
                        >
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faSlidersH} style={{ color: '#0284c7', fontSize: '0.9rem' }} />
                                <h6 className="fw-bold mb-0" style={{ color: isDark ? '#f8fafc' : '#0f172a', fontSize: '0.95rem' }}>
                                    Filtros de Búsqueda
                                </h6>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowBottomSheet(false)}
                                className="btn btn-sm p-1 rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                                style={{
                                    width: '28px',
                                    height: '28px',
                                    backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
                                    color: isDark ? '#cbd5e1' : '#64748b'
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.82rem' }} />
                            </button>
                        </div>

                        {/* Cuerpo con lista de Filtros */}
                        <div className="d-flex flex-column gap-3 mb-2">
                            {/* 1. Asesor asignado */}
                            <div>
                                <label
                                    className="form-label mb-1 fw-medium d-flex align-items-center gap-2"
                                    style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#475569' }}
                                >
                                    <FontAwesomeIcon icon={faUserCheck} style={{ fontSize: '0.74rem', color: '#0284c7' }} />
                                    <span>Asesor asignado</span>
                                </label>
                                <AgentFilterSelect
                                    variant="chip"
                                    className="w-100"
                                    hideIcon={true}
                                    selectedAgentId={selectedAgentId}
                                    onSelectAgent={setSelectedAgentId}
                                    agents={agents}
                                    currentUserId={idUsuario}
                                    currentUserName={nombreCompleto}
                                    isLoadingAgents={isLoadingAgents}
                                />
                            </div>

                            {/* 2. Incidencia / Etiqueta */}
                            <div>
                                <label
                                    className="form-label mb-1 fw-medium d-flex align-items-center gap-2"
                                    style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#475569' }}
                                >
                                    <FontAwesomeIcon icon={faTags} style={{ fontSize: '0.74rem', color: '#0284c7' }} />
                                    <span>Tipo de incidencia</span>
                                </label>
                                <EtiquetaFilterSelect
                                    variant="chip"
                                    className="w-100"
                                    hideIcon={true}
                                    showIcon={false}
                                    selectedEtiquetaId={selectedEtiquetaId}
                                    onSelectEtiqueta={setSelectedEtiquetaId}
                                    etiquetas={etiquetas}
                                    isLoading={isLoadingEtiquetas}
                                />
                            </div>

                            {/* 3. Nivel de Soporte */}
                            <div>
                                <label
                                    className="form-label mb-1 fw-medium d-flex align-items-center gap-2"
                                    style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#475569' }}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '0.74rem', color: '#0284c7' }} />
                                    <span>Nivel de soporte</span>
                                </label>
                                <NivelFilterSelect
                                    variant="chip"
                                    className="w-100"
                                    hideIcon={true}
                                    showIcon={false}
                                    selectedNivelId={selectedNivelId}
                                    onSelectNivel={setSelectedNivelId}
                                />
                            </div>
                        </div>

                        {/* Botón de confirmación */}
                        <div className="mt-4">
                            <button
                                type="button"
                                className="btn btn-primary w-100 py-2 fw-semibold shadow-none"
                                style={{ borderRadius: '10px', fontSize: '0.88rem' }}
                                onClick={() => setShowBottomSheet(false)}
                            >
                                Ver resultados
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ticket List Cards / Loader / Empty State / Pagination */}
            {isLoading && tickets.length === 0 ? (
                <TicketsLoadingState />
            ) : tickets.length === 0 ? (
                <TicketsEmptyState filterTab={filterTab} hasSearch={Boolean(debouncedSearchTerm)} onReload={reloadTickets} />
            ) : (
                <>
                    <div className="d-flex flex-column gap-3">
                        {tickets.map(ticket => (
                            <TicketCardItem
                                key={ticket.idTicket}
                                ticket={ticket}
                                selectedAgentId={selectedAgentId}
                                agents={agents}
                                isLoadingAgents={isLoadingAgents}
                                onRequestAgents={fetchAgents}
                                onSelectTicket={handleSelectTicket}
                                onAssignTicketAgent={handleOpenAssignModal}
                            />
                        ))}
                    </div>

                    <TicketsPagination
                        currentPage={page}
                        totalPages={totalPages}
                        totalRecords={totalRegistros}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={size => {
                            setPageSize(size);
                            setPage(1);
                        }}
                        isLoading={isLoading}
                    />
                </>
            )}

            {/* MODAL DE ASIGNACIÓN DE ASESOR */}
            <AssignTicketToAgentList
                show={showAssignModal}
                onHide={() => setShowAssignModal(false)}
                ticket={ticketToAssign}
                agents={agents}
                currentUserId={idUsuario}
                currentUserName={nombreCompleto}
                onConfirm={handleConfirmAssignTicket}
            />
        </div>
    );
};

export default TicketsView;